import type { ChannelClient } from '@hibro/platform-core';
import type { ILauncherService, LaunchItem, RailPosition } from '@hibro/shared';
import { LAUNCHER_CHANGED_EVENT } from '@hibro/shared';
import { Button, Checkbox, Icon } from '@hibro/ui-client';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AppLinkIcon } from '../app-links/icon';
import { useTranslator } from '../i18n/i18n';

const SEGMENTS: readonly RailPosition[] = ['top', 'middle', 'bottom'];

const SEGMENT_TITLE: Record<RailPosition, string> = {
  top: 'settings.rail.section.top',
  middle: 'settings.rail.section.middle',
  bottom: 'settings.rail.section.bottom',
};

export interface RailLayoutSectionProps {
  launcherService: ILauncherService;
  client: ChannelClient;
}

/**
 * Mechanism-level sidebar arrangement (见 21): pin/unpin, move up/down, reset.
 * No drag-and-drop — the shell has no flat-list DnD, and Tree's is not a fit.
 */
export function RailLayoutSection({
  launcherService,
  client,
}: RailLayoutSectionProps): ReactNode {
  const { t, title } = useTranslator();
  const [items, setItems] = useState<LaunchItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    launcherService
      .listItems()
      .then((next) => {
        setItems(next);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [launcherService]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    return client.listen(LAUNCHER_CHANGED_EVENT, () => {
      refresh();
    });
  }, [client, refresh]);

  const bySegment = useMemo(() => {
    const groups: Record<RailPosition, LaunchItem[]> = {
      top: [],
      middle: [],
      bottom: [],
    };
    for (const item of items) {
      groups[item.segment].push(item);
    }
    for (const segment of SEGMENTS) {
      groups[segment].sort((a, b) => a.order - b.order);
    }
    return groups;
  }, [items]);

  const resolveLabel = (item: LaunchItem): string => {
    if (item.source === 'builtin') {
      return t(item.title);
    }
    if (item.source === 'app-link' || item.source === 'provider') {
      return item.title;
    }
    return title(item.moduleId, item.title);
  };

  const run = (op: () => Promise<void>): void => {
    setBusy(true);
    setError(null);
    op()
      .then(refresh)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setBusy(false));
  };

  const move = (segment: RailPosition, key: string, delta: -1 | 1): void => {
    const pinned = bySegment[segment].filter((item) => item.pinned);
    const keys = pinned.map((item) => item.key);
    const index = keys.indexOf(key);
    const next = index + delta;
    if (index < 0 || next < 0 || next >= keys.length) {
      return;
    }
    const reordered = [...keys];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(next, 0, moved);
    run(() => launcherService.reorder(segment, reordered));
  };

  if (items.length === 0 && !error) {
    return (
      <p className="my-2 text-[13px] text-muted-foreground">
        {t('settings.rail.empty')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3 py-2">
      {error ? (
        <p className="m-0 text-[13px] text-destructive">{error}</p>
      ) : null}
      {SEGMENTS.map((segment) => {
        const segmentItems = bySegment[segment];
        if (segmentItems.length === 0) {
          return null;
        }
        const pinnedKeys = segmentItems
          .filter((item) => item.pinned)
          .map((item) => item.key);
        return (
          <div key={segment}>
            <h4 className="m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t(SEGMENT_TITLE[segment])}
            </h4>
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {segmentItems.map((item) => {
                const label = resolveLabel(item);
                const pinnedIndex = item.pinned
                  ? pinnedKeys.indexOf(item.key)
                  : -1;
                const pinnedCount = pinnedKeys.length;
                return (
                  <li
                    key={item.key}
                    className="flex items-center gap-2 rounded-lg px-1 py-1"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center">
                      {item.source === 'app-link' ||
                      item.action?.type === 'openUrl' ? (
                        <AppLinkIcon spec={item.icon} size={18} />
                      ) : item.icon ? (
                        <Icon spec={item.icon} fallbackText={label} size={18} />
                      ) : (
                        <span className="text-xs font-semibold">
                          {label.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px]">
                      {label}
                      {item.locked ? (
                        <span className="ml-1.5 text-[11px] text-muted-foreground">
                          ({t('settings.rail.locked')})
                        </span>
                      ) : null}
                    </span>
                    <Checkbox
                      checked={item.pinned}
                      disabled={busy || item.locked}
                      onCheckedChange={(checked) => {
                        if (item.locked) {
                          return;
                        }
                        run(() =>
                          launcherService.setPinned(item.key, checked === true),
                        );
                      }}
                      aria-label={
                        item.pinned
                          ? t('settings.rail.unpin')
                          : t('settings.rail.pin')
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy || !item.pinned || pinnedIndex <= 0}
                      onClick={() => move(segment, item.key, -1)}
                      aria-label={t('settings.rail.moveUp')}
                    >
                      <Icon
                        spec={{ type: 'builtin', id: 'arrow-up' }}
                        size={14}
                      />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={
                        busy ||
                        !item.pinned ||
                        pinnedIndex < 0 ||
                        pinnedIndex >= pinnedCount - 1
                      }
                      onClick={() => move(segment, item.key, 1)}
                      aria-label={t('settings.rail.moveDown')}
                    >
                      <Icon
                        spec={{ type: 'builtin', id: 'arrow-down' }}
                        size={14}
                      />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      <div className="pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => run(() => launcherService.resetLayout())}
        >
          {t('settings.rail.reset')}
        </Button>
      </div>
    </div>
  );
}
